import { useTranslation } from 'react-i18next';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from '@/shared/components/ui/Drawer';
import { Badge } from '@/shared/components/ui/Badge';
import { formatDate } from '@/shared/utils/date-utils';
import i18n from '@/i18n';
import { ReactNode } from 'react';

interface CurrentOccurrence {
  startDate: string;
}

interface BaseOccurrence {
  startDate: string;
  endDate: string;
}

interface OccurrenceHistoryDrawerProps<TOccurrence extends BaseOccurrence> {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  current?: CurrentOccurrence;
  history?: TOccurrence[];
  renderCurrentOccurrence: (current: CurrentOccurrence) => ReactNode;
  renderPastOccurrence: (occurrence: TOccurrence, index: number) => ReactNode;
  footerContent?: ReactNode;
  headerExtra?: ReactNode;
}

export function OccurrenceHistoryDrawer<TOccurrence extends BaseOccurrence>({
  title,
  isOpen,
  onClose,
  current,
  history,
  renderCurrentOccurrence,
  renderPastOccurrence,
  footerContent,
  headerExtra,
}: OccurrenceHistoryDrawerProps<TOccurrence>) {
  const { t } = useTranslation('goals');

  const hasPastOccurrences = history && history.length > 0;
  const hasHistory = hasPastOccurrences || current;

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      direction="left"
    >
      <DrawerContent className="w-full md:w-2/3 lg:w-1/2 xl:w-1/3">
        <DrawerHeader className="text-start">
          <DrawerTitle className="text-start">
            {t('occurrenceHistory')}
          </DrawerTitle>
          <DrawerDescription className="text-start">{title}</DrawerDescription>
          {headerExtra}
        </DrawerHeader>

        <div className="p-4 space-y-6 overflow-y-auto">
          {/* Current Occurrence */}
          {current && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-foreground">
                  {t('currentOccurrence')}
                </h3>
                <Badge variant="default">{t('currentBadge')}</Badge>
              </div>
              <div className="p-4 bg-primary/10 border-2 border-primary rounded-lg">
                {renderCurrentOccurrence(current)}
              </div>
            </div>
          )}

          {/* Past Occurrences */}
          {hasPastOccurrences && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">
                {t('pastOccurrences')}
              </h3>
              <div className="space-y-3">
                {history
                  ?.slice()
                  .reverse()
                  .map((occurrence, index) => (
                    <div
                      key={index}
                      className="p-4 bg-muted/50 border border-border rounded-lg"
                    >
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">
                            {t('period')}:
                          </span>
                          <span className="text-sm font-medium">
                            {formatDate(
                              occurrence.startDate,
                              'MMM d, yyyy',
                              i18n.language
                            )}{' '}
                            -{' '}
                            {formatDate(
                              occurrence.endDate,
                              'MMM d, yyyy',
                              i18n.language
                            )}
                          </span>
                        </div>
                        {renderPastOccurrence(occurrence, index)}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {!hasHistory && (
            <div className="text-center text-muted-foreground py-8">
              <p className="text-sm">{t('noHistory')}</p>
              <p className="text-xs">{t('noHistoryDescription')}</p>
            </div>
          )}
        </div>

        {footerContent && (
          <DrawerFooter className="border-t">{footerContent}</DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
}
